import { PRESS } from '../../palette';

/**
 * **可调参数表。**
 *
 * 评审要「留出做画面调优的空间」，所以画法里**不许出现魔数** ——
 * 一切"这个圆多大、那道条纹多宽、楔子什么角度"都从这里读。
 *
 * ## 怎么用
 *
 * ```
 *   改这里的数字  →  调优面板（debug/face.html）挂滑块实时改  →  满意了抄回这里
 * ```
 *
 * ⚠️ 改完要 `bumpTune()`，否则牌面缓存认为"内容没变"，不会重画。
 * 调优面板会自动调它；手改代码时不用管（重新加载页面就是新版本）。
 *
 * ⚠️ 这些数字的**后果**都记在 `docs/card-faces.md` 里。改之前先读那一节 ——
 * 有些数字是踩过坑之后才定下来的（比如"纸片必须比字宽出一圈"）。
 */

// ============================================================
// 版本号 —— 牌面缓存的键里有它
// ============================================================

let version = 1;

/** 当前调优版本。`CardArt.revision()` 必须把它编进去 */
export function tuneVersion(): number {
  return version;
}

/** 面板改完参数调它，让已经画好的 FBO 重画 */
export function bumpTune(): void {
  version++;
}

// ============================================================
// 全局：手绘与印刷
// ============================================================

export interface HandTune {
  /** 手抖幅度（设计单位）。0 = 尺子画的 */
  jitter: number;
  /** 笔压变化的幅度。⚠️ 手绘感的第一来源是它，不是抖动 */
  press: number;
}

export interface PrintTune {
  /** 网点间距（设计单位）。⚠️ 必须和背景半调成整数比，否则叠在一起会有摩尔纹 */
  halftonePitch: number;
  halftoneAngle: number;
  halftoneCoverage: number;
  /** 危险条纹 */
  hazardPitch: number;
  hazardAngle: number;
  hazardDuty: number;
}

/**
 * 文字基线的**兜底**比例。
 *
 * ⚠️ 正常情况下用不到它：`painter.ts` 用 Pixi 的字体度量**量**出真实的
 * ascent/descent，因为中文字体和等宽字体的 ascent 差得很远（0.88 vs 0.78），
 * 一个常数不可能同时撑对 —— 第一版就是这么把「异常」顶到 EXCEPTION 上的。
 *
 * 这里只留着当"字体还没加载、量不出来"时的退路。
 */
export const BASELINE_RATIO = 0.8;

export const HAND: HandTune = {
  jitter: 1.2,
  press: 0.32,
};

export const PRINT: PrintTune = {
  // 3.7 —— 背景的半调是 11，正好 3 倍。牌面比背景细一档，看起来才是"印上去的"
  halftonePitch: 3.7,
  halftoneAngle: 45,
  halftoneCoverage: 0.5,
  // 危险条纹要比网点粗得多，远看才是一条一条的
  hazardPitch: 5.5,
  hazardAngle: 45,
  hazardDuty: 0.5,
};

// ============================================================
// 逐卡
// ============================================================

/**
 * `Exception` 异常 —— 白卡。
 *
 * ⚠️ 这是**唯一**一张：白底、用 `PRESS.red`、零酸色、**不上印刷层**、
 * 外发光只在牌外可见。改这里之前先读 `visual-design.md` §2 和 §6.1。
 */
export interface ExceptionTune {
  /** 同心圆的圆心。⚠️ 刻意在牌**外**（x > 100）—— 它是一台仪器，不是装饰 */
  ringCx: number;
  ringCy: number;
  ringR1: number;
  ringR2: number;
  /** 红楔子：尖端 + 两条后缘 */
  wedgeTipX: number;
  wedgeTipY: number;
  wedgeBackX: number;
  wedgeTopY: number;
  wedgeBotY: number;
  /** 楔尖准星的臂长 */
  markArm: number;
  /** 底部危险条纹带 */
  hazardY: number;
  hazardH: number;
}

export const EXCEPTION: ExceptionTune = {
  ringCx: 105,
  ringCy: 112,
  ringR1: 78,
  ringR2: 64,
  wedgeTipX: 64,
  wedgeTipY: 82,
  wedgeBackX: -45,
  wedgeTopY: 8,
  wedgeBotY: 46,
  markArm: 7.5,
  hazardY: 129,
  hazardH: 11,
};

// ============================================================
// 两个新资产
// ============================================================

/**
 * **牌背**。
 *
 * ⚠️ 牌背**零酸色**，和五张无效牌同一个道理，但理由更强：
 * 它是**反面**。桌上任何一处出现酸色，就说明"这里有张牌是正面朝上的"。
 * 这是一个纯功能的色码 —— 一旦牌背沾了酸色，它就不成立了。
 */
export interface BackTune {
  /** 织纹的线屏 */
  linePitch: number;
  lineAngle: number;
  lineDuty: number;
  lineAlpha: number;
  /** 织纹的点屏。⚠️ 角度必须和线屏不同，否则没有摩尔纹 */
  dotPitch: number;
  dotAngle: number;
  dotCoverage: number;
  dotAlpha: number;
  /** 中央标记：两块版的尺寸与错位量 */
  plateW: number;
  plateH: number;
  ghostX: number;
  ghostY: number;
  /** 角标 */
  markInset: number;
  markArm: number;
}

export const BACK: BackTune = {
  // 4.6 / 0.07 → 约 0.32 单位粗的细线。太粗就从"织纹"变成"条纹"了
  linePitch: 4.6,
  lineAngle: 45,
  lineDuty: 0.07,
  // ⚠️ 两个 alpha 是**被一张图否掉过一次**的数字。第一版都是 0.5，
  //    叠出来是一张**浅灰**的牌背 —— 桌上十三张背面朝上的牌整个亮起来，
  //    而且中央那个黑框立刻读成"一个洞"。牌背必须先是黑的。
  lineAlpha: 0.16,
  dotPitch: 4.4,
  dotAngle: 15,
  dotCoverage: 0.1,
  dotAlpha: 0.22,
  // 26 × 36 —— 卡的比例（2.5 : 3.5）。再大就压过整张牌了
  plateW: 29,
  plateH: 40,
  ghostX: 5,
  ghostY: 3.6,
  markInset: 5.5,
  markArm: 9,
};

/**
 * **标题 Logo**。
 *
 * 设计空间不是卡的形状（见 `spec.ts` 的 `design`）——
 * 但走的是**同一条**绘制管线，所以它和牌背、牌面共用织纹和标记。
 */
export interface TitleTune {
  /** 两行词标各自占满的宽度。⚠️ 由 painter 量出来再缩，不是猜字号 */
  measure: number;
  /** 两行**初始**字号。它们决定两行的**高度比**；宽度会被 `fitWidth` 抹平 */
  line1Size: number;
  line2Size: number;
  line1Y: number;
  line2Y: number;
  /** 套印错位：两个色版各自偏移多少 */
  misX: number;
  misY: number;
  /** 中央标记（红楔子）的**包围盒中心** y */
  sigilY: number;
  /** 楔子长度 */
  wedgeLength: number;
  /** 印张带 */
  bandTop: number;
  bandBottom: number;
}

export const TITLE: TitleTune = {
  // ⚠️ 从 186 缩到 140，是为了给楔子让出分量。
  //    第一版把词标放到 186、楔子只有 40 长，结果那枚楔子读成一个**光标**，
  //    而不是游戏的标记 —— 标记必须和词标同量级，不能是它上面的一个装饰
  measure: 140,
  // 4 个字 vs 8 个字 → 字号比 2 : 1 时两行天然等宽，`fitWidth` 只做微调
  line1Size: 62,
  line2Size: 31,
  line1Y: 150,
  line2Y: 178,
  misX: 1.5,
  misY: 0.9,
  // ⚠️ 标记原来在 52，正好压在 ACID 的 C 上 —— 第一版就是这么错的。
  //    ACID 的字号是 84，帽高约 59，所以基线 118 时它的顶在 59 附近，
  //    标记必须整条让到 52 以下
  // ⚠️ 70 / 76 那一版**楔子压到了 ACID 的 A 和 C** —— 和之前"标记压在 C 上"
  //    是同一个错。楔子转 34° 之后最低点在左下角，算包围盒时要把它算进去：
  //    76 长时下缘到 y=102，而 ACID 的帽顶在 105 附近，只剩 3 单位，视觉上就是贴住了
  sigilY: 63,
  wedgeLength: 72,
  bandTop: 26,
  bandBottom: 184,
};

/** 标题 Logo 的设计空间。调用方按它算实际尺寸 */
export const TITLE_DESIGN = { width: 320, height: 224 } as const;

/**
 * **红楔子** —— 全套视觉的原点，现在也是游戏自己的标记。
 *
 * ⚠️ 它用的是 `PRESS.red`，而那个墨原先写着"只有异常牌能用"。
 * 现在的话是：**楔子作「标记」到处都可以，作「动作」只属于异常**。
 * 完整的分工写在 `docs/card-faces.md` §5.1。
 */
// ⚠️ 这里的字段**不写 `readonly`** —— 和 BACK / TITLE / HAND 保持一致。
//    这几张表存在的意义就是**给调优面板改**，标成只读会让面板那一行编译不过。
//    （踩过：WedgeTune 第一版写了 readonly，于是 art.ts 里 `WEDGE[key] = v` 报错）
export interface WedgeTune {
  red: number;
  /** 影子版。⚠️ 是**深红**不是黑 —— 否则读成"描边"，不是"印了两遍" */
  shadowRed: number;
  /** 标题 Logo 上那枚：接近原始海报的细长比 */
  titleLength: number;
  titleBackRatio: number;
  titleAngle: number;
  /** favicon 上那枚。⚠️ 比标题**胖**得多，理由见 `icon.ts` */
  iconBackRatio: number;
  iconAngle: number;
}

export const WEDGE: WedgeTune = {
  red: PRESS.red,
  shadowRed: 0x7a1410,
  // 海报上量下来是 109 : 38 ≈ 0.35
  titleLength: 40,
  titleBackRatio: 0.35,
  titleAngle: -34,
  // 2.2 : 1。⚠️ 不能再胖 —— 再胖就是"播放键"而不是"楔子"
  iconBackRatio: 0.455,
  iconAngle: -38,
};
